import React, { Suspense, lazy, useEffect } from "react";
import { Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { ToastProvider } from "./context/ToastContext";
import { GuestOnly, RequireAuth, RequireSuperUser } from "./components/guards";
import AppShell from "./components/layout/AppShell";
import { Spinner } from "./components/ui";
import Landing from "./pages/Landing";
import Login from "./pages/auth/Login";
import Register from "./pages/auth/Register";
import Forgot from "./pages/auth/Forgot";
import Chat from "./pages/app/Chat";
import NotFound from "./pages/NotFound";

const ToolsHome = lazy(() => import("./pages/app/ToolsPage").then((m) => ({ default: m.ToolsHome })));
const ToolRun = lazy(() => import("./pages/app/ToolsPage").then((m) => ({ default: m.ToolRun })));
const Studio = lazy(() => import("./pages/app/Studio"));
const Models = lazy(() => import("./pages/app/Models"));
const Pricing = lazy(() => import("./pages/app/Pricing"));
const Account = lazy(() => import("./pages/app/Account"));
const Settings = lazy(() => import("./pages/app/Settings"));
const AdminShell = lazy(() => import("./components/layout/AdminShell"));
const Dashboard = lazy(() => import("./pages/admin/Dashboard"));
const Users = lazy(() => import("./pages/admin/Users"));
const Payments = lazy(() => import("./pages/admin/Payments"));
const PricingAdmin = lazy(() => import("./pages/admin/PricingAdmin"));
const ModelsAdmin = lazy(() => import("./pages/admin/ModelsAdmin"));

const Fallback = () => (
  <div className="page-center">
    <Spinner size={26} />
  </div>
);

/** ⌘K / Ctrl+K = แชทใหม่ */
function Hotkeys() {
  const navigate = useNavigate();
  useEffect(() => {
    const fn = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        navigate("/chat", { state: { fresh: Date.now() } });
      }
    };
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, [navigate]);
  return null;
}

export default function App() {
  return (
    <ThemeProvider>
      <ToastProvider>
        <AuthProvider>
          <Hotkeys />
          <Suspense fallback={<Fallback />}>
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route element={<GuestOnly />}>
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot" element={<Forgot />} />
              </Route>
              <Route path="/su" element={<Login admin />} />

              <Route element={<AppShell />}>
                <Route path="/chat" element={<Chat />} />
                <Route path="/chat/:id" element={<Chat />} />
                <Route path="/tools" element={<ToolsHome />} />
                <Route path="/tools/:id" element={<ToolRun />} />
                <Route path="/image" element={<Studio kind="image" />} />
                <Route path="/video" element={<Studio kind="video" />} />
                <Route path="/models" element={<Models />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route element={<RequireAuth />}>
                  <Route path="/account" element={<Account />} />
                  <Route path="/settings" element={<Settings />} />
                </Route>
              </Route>

              <Route element={<RequireSuperUser />}>
                <Route path="/admin" element={<AdminShell />}>
                  <Route index element={<Dashboard />} />
                  <Route path="users" element={<Users />} />
                  <Route path="payments" element={<Payments />} />
                  <Route path="pricing" element={<PricingAdmin />} />
                  <Route path="models" element={<ModelsAdmin />} />
                </Route>
              </Route>

              <Route path="/home" element={<Navigate to="/chat" replace />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </AuthProvider>
      </ToastProvider>
    </ThemeProvider>
  );
}
